import { useEffect, useMemo, useRef } from "react";
import { useGetLanguagesQuery } from "@redux/features/languages/languagesApi";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { isControlledDropdownValueValid } from "@reusables/dropdowns/BaseDropdown/logic";
import { Language } from "@/types/general";

/**
 * Minimal form shape required by the component.
 * Compatible with both PurchaseOrdersFormTyping and SaleOrdersFormTyping.
 */
type LanguageFormFields = {
    language?: Language.Root | null;
    mode: "creation" | "editing";
};

type LanguageDropdownSectionProps = {
    storageKey: string;
    entityId?: number;
    entityLanguage?: { id: number } | null;
    isEntityDefaultPending?: boolean;
    skipInitialEntityHydration?: boolean;
    translationKeyPrefix: string;
};

/**
 * Language dropdown section for PurchaseOrders and SaleOrders forms.
 * @param storageKey Storage key to use for remembering last selected language
 * @param entityId Selected customer/supplier id
 * @param entityLanguage Entity language (if we need to supply a custom value)
 * @param isEntityDefaultPending Whether the selected entity is still loading its defaults
 * @param translationKeyPrefix Translation key prefix to use for translations
 * @constructor
 */
export default function LanguageDropdownSection({
    storageKey,
    entityId,
    entityLanguage,
    isEntityDefaultPending = false,
    skipInitialEntityHydration = false,
    translationKeyPrefix,
}: LanguageDropdownSectionProps) {
    const { t } = useTranslation("", { keyPrefix: translationKeyPrefix });
    const { data: languageOptions = [], isLoading: isLanguageOptionsLoading } =
        useGetLanguagesQuery();
    const { control, setValue, getValues, resetField } = useFormContext<LanguageFormFields>();

    const mode = useWatch({
        control,
        name: "mode",
    });

    const previousEntityIdRef = useRef<number | undefined>();
    const hydratedEntityIdRef = useRef<number | undefined>();
    const hasSkippedInitialEntityHydrationRef = useRef(false);

    const defaultLanguage = useMemo(
        () => languageOptions.find((language) => language.id === entityLanguage?.id),
        [entityLanguage?.id, languageOptions]
    );

    // Remembered language is only a fallback. If a customer/supplier is selected, wait until its
    // defaults are resolved and only then allow BaseDropdown to restore the remembered value when
    // no usable entity language exists.
    const rememberLastValueByKey =
        mode === "creation" && (!entityId || (!isEntityDefaultPending && !defaultLanguage))
            ? storageKey
            : undefined;

    useEffect(() => {
        if (mode !== "creation") {
            previousEntityIdRef.current = entityId;
            hydratedEntityIdRef.current = entityId;
            return;
        }

        if (previousEntityIdRef.current === entityId) {
            return;
        }

        if (skipInitialEntityHydration && !hasSkippedInitialEntityHydrationRef.current) {
            hasSkippedInitialEntityHydrationRef.current = true;
            previousEntityIdRef.current = entityId;
            hydratedEntityIdRef.current = entityId;
            return;
        }

        // Language depends on the currently selected customer/supplier. Clear the stale value on
        // entity changes so the next hydration pass can choose between the entity default and the
        // remembered fallback for the new context.
        resetField("language");
        hydratedEntityIdRef.current = undefined;
        previousEntityIdRef.current = entityId;
    }, [entityId, mode, resetField, skipInitialEntityHydration]);

    useEffect(() => {
        if (
            mode !== "creation" ||
            !entityId ||
            isEntityDefaultPending ||
            !defaultLanguage ||
            !languageOptions.length ||
            hydratedEntityIdRef.current === entityId
        ) {
            return;
        }

        const currentLanguage = getValues("language");

        // Once the selected customer/supplier exposes a default language, that hint wins over
        // remembered state. BaseDropdown only restores remembered values when this effect leaves
        // the field empty because no usable entity default exists.
        if (
            isControlledDropdownValueValid(
                currentLanguage,
                languageOptions,
                (language) => language.id
            )
        ) {
            hydratedEntityIdRef.current = entityId;
            return;
        }

        setValue("language", defaultLanguage, { shouldDirty: false });
        hydratedEntityIdRef.current = entityId;
    }, [
        defaultLanguage,
        entityId,
        getValues,
        isEntityDefaultPending,
        languageOptions,
        mode,
        setValue,
    ]);

    return (
        <Controller
            control={control}
            name="language"
            render={({ field, fieldState }) => (
                <BaseDropdown
                    {...field}
                    {...fieldState}
                    options={languageOptions}
                    getter={{
                        key: (opt) => opt.id,
                        label: (opt) => opt.name,
                    }}
                    label={t("label")}
                    placeholder={t("placeholder")}
                    isLoading={isLanguageOptionsLoading}
                    autocomplete
                    autofill={mode === "creation"}
                    rememberLastValueByKey={rememberLastValueByKey}
                />
            )}
        />
    );
}
